<template>
  <div class="promos-page">
    <!-- Page header -->
    <header class="page-head">
      <h1>Promo Codes <small class="admin-note">(Moderators)</small></h1>
      <p class="sub">Create, search, and manage promo codes without leaving this page.</p>
    </header>

    <!-- Toast notification -->
    <p v-if="statusMessage" :class="['toast', statusType]">
      {{ statusMessage }}
      <button class="toast__dismiss" @click="clearStatus" aria-label="Dismiss">×</button>
    </p>

    <!-- Create form -->
    <section class="card">
      <header class="card__head">
        <h2>Create a Promo Code</h2>
        <div class="row-actions">
       <!--   <button class="btn btn--ghost small" @click="generateCode" title="Generate code">Generate</button>-->
          <button class="btn small" @click="submitCreate">Create</button>
        </div>
      </header>

      <div class="form-grid">
        <div class="form-col">
          <div class="form-row">
            <label for="code">Code</label>
            <div class="with-append">
              <input
                id="code"
                v-model.trim="form.code"
                placeholder="e.g. LAUNCH20"
                maxlength="50"
                required
                @keydown.enter.prevent="submitCreate"
              />
              <button class="icon-btn" title="Copy" @click="copy(form.code)" :disabled="!form.code">📋</button>
            </div>
            <small :class="{'err': !isCodeValid && form.code}">Letters, numbers, dashes/underscores; 3–50 chars.</small>
          </div>

          <div class="form-row">
            <label for="effect">Effect</label>
            <select id="effect" v-model="form.effect_type">
              <option value="credits">Add credits</option>
              <option value="unlimited">Unlimited access</option>
            </select>
          </div>
<div class="form-row">
  <label for="scope">Scope</label>
  <select id="scope" v-model="form.scope">
    <option value="normal">Normal tools only</option>
    <option value="linkcheck">LinkCheck only</option>
    <option value="both">Both</option>
  </select>
</div>

<!-- Existing "Credits to add" stays for normal -->
<div class="form-row" v-if="form.effect_type === 'credits' && (form.scope === 'normal' || form.scope === 'both')">
  <label for="credits">Normal credits</label>
  <input id="credits" type="number" v-model.number="form.credits_amount" min="1" max="20" required />
  <small :class="{'err': !isCreditsValid}">Between 1 and 20.</small>
</div>

<!-- New: LinkCheck credits -->
<div class="form-row" v-if="form.effect_type === 'credits' && (form.scope === 'linkcheck' || form.scope === 'both')">
  <label for="lc_credits">LinkCheck credits</label>
  <input id="lc_credits" type="number" v-model.number="form.lc_credits_amount" min="1" max="20" required />
  <small :class="{'err': !isLcCreditsValid}">Between 1 and 20.</small>
</div>

        </div>

        <div class="form-col">
          <div class="form-row">
            <label for="usage">Usage limit</label>
            <input
              id="usage"
              type="number"
              v-model.number="form.usage_limit"
              min="1"
              max="1000"
              required
            />
            <small :class="{'err': !isUsageValid}">Total uses allowed. Default 1.</small>
          </div>

          <div class="form-row">
            <label for="expires">Expires at (optional)</label>
            <input id="expires" type="datetime-local" v-model="form.expires_at" />
            <small :class="{'err': !isExpiryValid && form.expires_at}">Leave blank for no expiry.</small>
          </div>

          <div class="form-row">
            <label for="notes">Notes (optional)</label>
            <textarea id="notes" v-model.trim="form.notes" rows="2" placeholder="Internal note"></textarea>
          </div>

          <div class="form-row -inline">
            <label class="checkbox">
              <input type="checkbox" v-model="form.is_active" />
              Active
            </label>
          </div>
        </div>
      </div>
    </section>

    <!-- Filters -->
    <section class="card -tight">
      <div class="filters">
        <div class="filter-row">
          <input
            v-model.trim="q"
            @keydown.enter="applyFilters"
            placeholder="Search code or notes…"
            class="search"
          />
          <div class="filter-chip">
            <span>Status:</span>
            <button class="chip" :class="{active: activeFilter === ''}" @click="setActiveFilter('')">All</button>
            <button class="chip" :class="{active: activeFilter === '1'}" @click="setActiveFilter('1')">Active</button>
            <button class="chip" :class="{active: activeFilter === '0'}" @click="setActiveFilter('0')">Inactive</button>
          </div>
          <button class="btn btn--ghost" @click="applyFilters">Apply</button>
          <button class="btn btn--ghost" v-if="q || activeFilter" @click="clearFilters">Clear</button>
        </div>
      </div>
    </section>

    <!-- Table card -->
    <section class="card">
      <header class="card__head">
        <h2>Codes</h2>
        <span class="muted" v-if="totalPages">Page {{ page }} of {{ totalPages }}</span>
      </header>

      <!-- Loading state -->
      <div v-if="isLoading" class="skeleton">
        <div class="skeleton-row" v-for="n in 6" :key="'sk-'+n"></div>
      </div>

      <div v-else>
        <div v-if="rows.length" class="table-wrap">
          <table class="nice-table">
            <thead>
              <tr>
                <th style="width:180px;">Code</th>
                <th>Effect</th>
                <th>Usage</th>
                <th>Expires</th>
                <th>Active</th>
                <th>Created By</th>
                <th>Created</th>
                <th>Notes</th>
                <th style="width:180px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in rows" :key="r.id">
                <td class="mono truncate">
                  <span :title="r.code">{{ r.code }}</span>
                  <button class="icon-btn" title="Copy code" @click="copy(r.code)">📋</button>
                </td>
         <td>
  <template v-if="r.effect_type === 'credits'">
    <span v-if="r.scope === 'normal' || r.scope === 'both'" class="pill pill--info">
      +{{ r.credits_amount || 0 }} normal
    </span>
    <span v-if="r.scope === 'linkcheck' || r.scope === 'both'" class="pill pill--info" style="margin-left:6px">
      +{{ r.lc_credits_amount || 0 }} LinkCheck
    </span>
  </template>
  <template v-else>
    <span class="pill pill--ok">
      Unlimited ({{ r.scope }})
    </span>
  </template>
</td>

                <td class="semi">{{ r.used_count }} / {{ r.usage_limit }}</td>
                <td>{{ r.expires_at ? formatTime(r.expires_at) : '—' }}</td>
                <td>
                  <span class="pill" :class="r.is_active ? 'pill--ok' : 'pill--muted'">
                    {{ r.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>{{ r.created_by || '—' }}</td>
                <td class="semi">{{ formatTime(r.created_at) }}</td>
                <td class="notes"><small class="muted">{{ r.notes || '—' }}</small></td>
                <td>
                  <div class="row-actions">
                    <button class="btn btn--ghost small" @click="toggleActive(r)">
                      {{ r.is_active ? 'Deactivate' : 'Activate' }}
                    </button>
                    <button class="btn btn--danger small" @click="openDelete(r)">Delete</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="empty">No promo codes found.</p>

        <!-- Mobile cards -->
        <div class="cards" v-if="rows.length">
          <article v-for="r in rows" :key="'m-'+r.id" class="mcard">
            <header class="mcard__head">
              <div class="mcard__title">
                <span class="mono">{{ r.code }}</span>
              </div>
              <span class="pill" :class="r.is_active ? 'pill--ok' : 'pill--muted'">{{ r.is_active ? 'Active' : 'Inactive' }}</span>
            </header>
            <dl class="kv">
              <div>
                <dt>Effect</dt>
                <dd>
                  <span v-if="r.effect_type === 'credits'" class="pill pill--info">+{{ r.credits_amount }} credits</span>
                  <span v-else class="pill pill--ok">Unlimited</span>
                </dd>
              </div>
              <div>
                <dt>Usage</dt>
                <dd class="semi">{{ r.used_count }} / {{ r.usage_limit }}</dd>
              </div>
              <div>
                <dt>Expires</dt>
                <dd>{{ r.expires_at ? formatTime(r.expires_at) : '—' }}</dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd class="semi">{{ formatTime(r.created_at) }}</dd>
              </div>
              <div>
                <dt>By</dt>
                <dd>{{ r.created_by || '—' }}</dd>
              </div>
              <div>
                <dt>Notes</dt>
                <dd class="prewrap muted">{{ r.notes || '—' }}</dd>
              </div>
            </dl>
            <div class="mcard__actions">
              <button class="btn btn--ghost small" @click="toggleActive(r)">{{ r.is_active ? 'Deactivate' : 'Activate' }}</button>
              <button class="btn btn--danger small" @click="openDelete(r)">Delete</button>
            </div>
          </article>
        </div>

        <!-- Pagination -->
        <div class="pagination" v-if="total > limit">
          <button class="btn btn--ghost" @click="prevPage" :disabled="page === 1">Previous</button>
          <span>Page {{ page }} of {{ totalPages }}</span>
          <button class="btn btn--ghost" @click="nextPage" :disabled="page >= totalPages">Next</button>
        </div>
      </div>
    </section>

    <!-- Delete confirmation modal -->
    <div v-if="deleteTarget" class="modal" role="dialog" aria-modal="true" aria-labelledby="deleteTitle">
      <div class="modal__panel">
        <h3 id="deleteTitle">Delete promo code?</h3>
        <p>Delete <strong>{{ deleteTarget.code }}</strong>? This cannot be undone.</p>
        <div class="modal__actions">
          <button class="btn btn--danger" @click="confirmDelete">Yes, delete</button>
          <button class="btn btn--ghost" @click="deleteTarget = null">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'PromoCodeManagerNew',
  data() {
    return {
      // status toast
      statusMessage: '',
      statusType: '', // 'success' | 'error'

      // create form
      form: {
        code: '',
        effect_type: 'credits', // 'credits' | 'unlimited'
        credits_amount: 10,
        usage_limit: 1,
        expires_at: '', // yyyy-MM-ddTHH:mm
        is_active: true,
        notes: ''
      },

      // list
      rows: [],
      page: 1,
      limit: 25,
      total: 0,
      q: '',
      activeFilter: '',

      // ui state
      isLoading: false,
      deleteTarget: null
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.limit) || 1
    },
    isCodeValid() {
      return /^[A-Za-z0-9_-]{3,50}$/.test(this.form.code || '')
    },
 isCreditsValid() {
    if (this.form.effect_type !== 'credits') return true;
    if (this.form.scope === 'linkcheck') return true; // not required
    const n = Number(this.form.credits_amount);
    return Number.isInteger(n) && n >= 1 && n <= 20;
  },
  isLcCreditsValid() {
    if (this.form.effect_type !== 'credits') return true;
    if (this.form.scope === 'normal') return true; // not required
    const n = Number(this.form.lc_credits_amount);
    return Number.isInteger(n) && n >= 1 && n <= 20;
  },
    isUsageValid() {
      const lim = Number(this.form.usage_limit)
      return Number.isInteger(lim) && lim >= 1 && lim <= 1000
    },
    isExpiryValid() {
      return !this.form.expires_at || !isNaN(new Date(this.form.expires_at))
    }
  },

  mounted() {
    this.fetchRows();
  },
  methods: {
    // --- helpers ---
    fmtExpiresForApi(localVal) {
      // Convert datetime-local to 'YYYY-MM-DD HH:MM:SS'
      if (!localVal) return null
      const d = new Date(localVal)
      if (isNaN(d)) return null
      const pad = (n) => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`
    },
    show(message, type = 'success') {
      this.statusMessage = message
      this.statusType = type
      clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => {
        this.statusMessage = ''
        this.statusType = ''
      }, 3500)
    },
    clearStatus() {
      this.statusMessage = ''
      this.statusType = ''
      clearTimeout(this._toastTimer)
    },
    formatTime(iso) {
      const d = new Date(iso)
      return isNaN(d.getTime()) ? iso : d.toLocaleString()
    },
    copy(text) {
      if (!text) return
      navigator.clipboard?.writeText(text).then(() => this.show('Copied to clipboard.')).catch(() => {})
    },

    // --- validation ---
    validateForm() {
      if (!this.isCodeValid) {
        this.show('Invalid code format.', 'error')
        return false
      }
      if (!this.isCreditsValid) {
        this.show('Credits must be 1–20.', 'error')
        return false
      }
      if (!this.isUsageValid) {
        this.show('Usage limit must be 1–1000.', 'error')
        return false
      }
      if (!this.isExpiryValid) {
        this.show('Invalid expiration date.', 'error')
        return false
      }
      return true
    },

    // --- API ---
    fetchRows() {
      this.isLoading = true
      const params = new URLSearchParams({ page: this.page, limit: this.limit })
      if (this.q) params.set('q', this.q)
      if (this.activeFilter !== '') params.set('active', this.activeFilter)

      axios
        .get(`/etherscan/list_promo_codes.php?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        .then((res) => {
          if (res.data.status === 'success') {
            this.rows = res.data.rows || []
            this.total = res.data.total || 0
          } else {
            this.show(res.data.message || 'Failed to load promo codes.', 'error')
          }
        })
        .catch(() => this.show('Failed to load promo codes.', 'error'))
        .finally(() => (this.isLoading = false))
    },

    submitCreate() {
      if (!this.validateForm()) return

const payload = {
  code: this.form.code.trim(),
  effect_type: this.form.effect_type,        // 'credits' | 'unlimited'
  scope: this.form.scope,                    // 'normal' | 'linkcheck' | 'both'
  credits_amount: this.form.scope !== 'linkcheck'
    ? Number(this.form.credits_amount) : null,
  lc_credits_amount: this.form.scope !== 'normal'
    ? Number(this.form.lc_credits_amount) : null,
  usage_limit: Number(this.form.usage_limit),
  expires_at: this.fmtExpiresForApi(this.form.expires_at),
  is_active: !!this.form.is_active,
  notes: this.form.notes?.trim() || ''
};

      axios
        .post('/etherscan/create_promo_code.php', payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        .then((res) => {
          if (res.data.status === 'success') {
            this.show('Promo code created.')
            this.resetForm()
            this.page = 1
            this.fetchRows()
          } else {
            this.show(res.data.message || 'Failed to create promo code.', 'error')
          }
        })
        .catch(() => this.show('Failed to create promo code.', 'error'))
    },

    toggleActive(row) {
      axios
        .post(
          '/etherscan/toggle_promo_code.php',
          { id: row.id, enable: !row.is_active },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        )
        .then((res) => {
          if (res.data.status === 'success') {
            this.show('Promo code updated.')
            this.fetchRows()
          } else {
            this.show(res.data.message || 'Failed to update promo code.', 'error')
          }
        })
        .catch(() => this.show('Failed to update promo code.', 'error'))
    },

    openDelete(row) {
      this.deleteTarget = row
    },

    confirmDelete() {
      if (!this.deleteTarget) return
      axios
        .post(
          '/etherscan/delete_promo_code.php',
          { id: this.deleteTarget.id },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        )
        .then((res) => {
          if (res.data.status === 'success') {
            this.show('Promo code deleted.')
            this.fetchRows()
          } else {
            this.show(res.data.message || 'Failed to delete promo code.', 'error')
          }
        })
        .catch(() => this.show('Failed to delete promo code.', 'error'))
        .finally(() => (this.deleteTarget = null))
    },

    // --- paging+filters ---
    applyFilters() {
      this.page = 1
      this.fetchRows()
    },
    clearFilters() {
      this.q = ''
      this.activeFilter = ''
      this.page = 1
      this.fetchRows()
    },
    setActiveFilter(v) {
      this.activeFilter = v;
      this.applyFilters();
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchRows()
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page--
        this.fetchRows()
      }
    },

    resetForm() {
      this.form = {
        code: '',
        effect_type: 'credits',
        credits_amount: 10,             // normal credits
  lc_credits_amount: null,        // linkcheck credits (if applicable)
        usage_limit: 1,
        expires_at: '',
        is_active: true,
        notes: ''
      }
    },

    generateCode() {
      // Simple readable generator (e.g., BETA-7K3Q)
      const seg = () => Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4)
      this.form.code = `${['ALPHA','BETA','LAUNCH','TRIAL','ACCESS'][Math.floor(Math.random()*5)]}-${seg()}`
    }
  }
}
</script>

<style scoped>
/* Page */
.promos-page { max-width: 1120px; margin: 0 auto; padding: 1rem; color:#1f2a37; }
.page-head h1 { margin: 0; font-size: 1.5rem; font-weight: 800; color:#0f2740; }
.sub { color:#667085; margin:.15rem 0 .8rem; }
.admin-note { font-size:.8rem; color:#94a3b8; }

/* Toasts */
.toast { position: relative; padding:.6rem .8rem; border-radius:10px; margin:.2rem 0 1rem; font-weight:600; border:1px solid transparent; }
.toast.success { background:#ecfdf5; color:#065f46; border-color:#a7f3d0; }
.toast.error   { background:#fef2f2; color:#991b1b; border-color:#fecaca; }
.toast__dismiss { position:absolute; top:6px; right:8px; border:none; background:transparent; font-size:1.1rem; cursor:pointer; }

/* Cards */
.card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; box-shadow:0 10px 20px rgba(16,24,40,.06); padding:1rem; margin-bottom:1rem; }
.card.-tight { padding:.8rem .9rem; }
.card__head { display:flex; justify-content:space-between; align-items:center; margin-bottom:.6rem; }
.muted { color:#6b7280; }

/* Form */
.form-grid { display:grid; grid-template-columns:1fr 1fr; gap:1rem; }
.form-col { display:grid; gap:.6rem; }
.form-row { display:flex; flex-direction:column; gap:.25rem; }
.form-row label { font-weight:700; color:#0f2740; }
.form-row small { color:#6b7280; }
.form-row small.err { color:#b91c1c; }
.with-append { display:flex; gap:.4rem; align-items:center; }
.checkbox { display:flex; align-items:center; gap:.45rem; }
.search { min-width: 260px; }

/* Filters */
.filters { display:flex; flex-wrap:wrap; gap:.6rem; }
.filter-row { display:flex; gap:.6rem; align-items:center; width:100%; }
.filter-chip span { color:#475467; margin-right:.35rem; }
.chip { border:1px solid #e5e7eb; background:#f9fafb; padding:.35rem .6rem; border-radius:10px; cursor:pointer; font-weight:600; }
.chip.active { background:#eef2ff; border-color:#c7d2fe; color:#4338ca; }

/* Buttons */
.btn { padding:.55rem .8rem; border-radius:10px; border:1px solid rgba(15,38,61,.15); background:linear-gradient(180deg,#ffffff,#f3f4f6); cursor:pointer; font-weight:600; }
.btn:hover { background:linear-gradient(180deg,#f9fafb,#eef2f7); }
.btn--ghost { background:#f9fafb; border:1px solid #e5e7eb; }
.btn--danger { background:#fee2e2; border:1px solid #fecaca; color:#991b1b; }
.small { padding:.4rem .6rem; font-size:.9rem; }
.row-actions { display:flex; gap:.4rem; flex-wrap:wrap; }
.icon-btn { border:1px solid #e5e7eb; background:#fff; border-radius:8px; padding:.1rem .35rem; cursor:pointer; }
.icon-btn:hover { background:#f3f4f6; }

/* Skeleton */
.skeleton { display:grid; gap:.5rem; }
.skeleton-row { height:42px; background:linear-gradient(90deg,#f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%); background-size:400% 100%; animation:shimmer 1.2s infinite; border-radius:10px; }
@keyframes shimmer { 0%{background-position:100% 0} 100%{background-position:0 0} }

/* Table */
.table-wrap { overflow-x:auto; border:1px solid #e5e7eb; border-radius:12px; }
.nice-table { width:100%; border-collapse:separate; border-spacing:0; min-width:880px; table-layout:fixed; }
.nice-table thead th { position:sticky; top:0; z-index:2; background:#f3f6fb; color:#0f2740; text-align:left; font-weight:700; font-size:.92rem; border-bottom:1px solid #e5e7eb; padding:.7rem .7rem; }
.nice-table td { padding:.6rem .7rem; border-bottom:1px solid #eef1f5; vertical-align:top; }
.truncate { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; }
.semi { font-weight:600; }
.notes { max-width: 280px; }

/* Pills */
.pill { display:inline-block; padding:.15rem .5rem; border-radius:999px; font-size:.78rem; font-weight:700; border:1px solid transparent; }
.pill--ok { background:#e8faf3; color:#065f46; border-color:#a7f3d0; }
.pill--muted { background:#f3f4f6; color:#374151; border-color:#e5e7eb; }
.pill--info { background:#eef2ff; color:#4338ca; border-color:#c7d2fe; }

/* Empty */
.empty { color:#6b7280; padding:.5rem 0; text-align:center; }

/* Pagination */
.pagination { display:flex; gap:.6rem; align-items:center; justify-content:center; margin-top:.8rem; }

/* Mobile cards */
.cards { display:none; }
@media (max-width: 760px) {
  .form-grid { grid-template-columns:1fr; }
  .table-wrap { display:none; }
  .cards { display:grid; gap:.8rem; }
  .mcard { border:1px solid #e5e7eb; border-radius:12px; background:#fff; box-shadow:0 6px 14px rgba(16,24,40,.06); padding:.9rem; }
  .mcard__head { display:flex; align-items:center; justify-content:space-between; gap:.6rem; margin-bottom:.35rem; }
  .mcard__title { font-weight:700; }
  .kv { display:grid; gap:.35rem; }
  .kv > div { display:grid; grid-template-columns: 90px 1fr; gap:.4rem; align-items:baseline; }
  .kv dt { color:#6b7280; font-size:.86rem; }
  .kv dd { margin:0; }
  .prewrap { white-space:pre-wrap; }
  .mcard__actions { margin-top:.6rem; display:flex; flex-wrap:wrap; gap:.4rem; }
}

/* Modal */
.modal { position:fixed; inset:0; background:rgba(0,0,0,.45); display:grid; place-items:center; z-index:1000; }
.modal__panel { background:#fff; border-radius:14px; border:1px solid #e5e7eb; box-shadow:0 20px 50px rgba(0,0,0,.25); padding:1rem; width:min(460px, 92vw); }
.modal__panel h3 { margin:0 0 .35rem; }
.modal__actions { display:flex; gap:.5rem; justify-content:flex-end; margin-top:.6rem; }
</style>
